import { useCallback, useEffect, useState } from 'react';

// SVG backgrounds for each spotlight word
// Use ?url suffix to ensure Vite imports these as URL strings (not React components via svgr)
import hiddenTalentsSvg from './hidden-talents.svg?url';
import personalCatchphraseSvg from './personal-catchphrase.svg?url';
import recommendedReadingSvg from './recommended-reading.svg?url';
import superpowersSvg from './superpowers.svg?url';
import topSupportersSvg from './top-supporters.svg?url';
import topValuesSvg from './top-values.svg?url';

export interface SpotlightWordConfig {
	/** Translation key for the word */
	labelKey: string;
	/** SVG background URL */
	backgroundSvg: string;
}

/**
 * Configuration for each spotlight section type with its paired SVG background.
 * Order matches the Figma design.
 */
export const SPOTLIGHT_WORDS: SpotlightWordConfig[] = [
	{ labelKey: 'topSupporters', backgroundSvg: topSupportersSvg },
	{ labelKey: 'topValues', backgroundSvg: topValuesSvg },
	{ labelKey: 'superpowers', backgroundSvg: superpowersSvg },
	{ labelKey: 'personalCatchphrase', backgroundSvg: personalCatchphraseSvg },
	{ labelKey: 'hiddenTalents', backgroundSvg: hiddenTalentsSvg },
	{ labelKey: 'recommendedReading', backgroundSvg: recommendedReadingSvg },
];

export interface UseRotatingSpotlightWordOptions {
	/** Interval in milliseconds between word changes. Default: 3000ms */
	interval?: number;
	/** Whether to pause the rotation. Default: false */
	paused?: boolean;
}

export interface UseRotatingSpotlightWordReturn {
	/** Current word configuration */
	current: SpotlightWordConfig;
	/** Index of the current word (0-5) */
	currentIndex: number;
	/** Manually go to next word */
	next: () => void;
	/** Manually go to previous word */
	previous: () => void;
}

/**
 * Hook that cycles through spotlight section types at a regular interval.
 * Returns the current word and its SVG background for animation.
 */
export const useRotatingSpotlightWord = ({ interval = 3000, paused = false }: UseRotatingSpotlightWordOptions = {}): UseRotatingSpotlightWordReturn => {
	const [currentIndex, setCurrentIndex] = useState(0);

	const next = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % SPOTLIGHT_WORDS.length);
	}, []);

	const previous = useCallback(() => {
		setCurrentIndex((prev) => (prev - 1 + SPOTLIGHT_WORDS.length) % SPOTLIGHT_WORDS.length);
	}, []);

	useEffect(() => {
		if (paused) return;

		const timer = setInterval(next, interval);

		return () => clearInterval(timer);
	}, [interval, paused, next]);

	return {
		current: SPOTLIGHT_WORDS[currentIndex],
		currentIndex,
		next,
		previous,
	};
};
